/**
 * Onglet "Devis" / "Factures" / "Contrats" du back-office B2B.
 *
 * Composant réutilisable contrôlé par la prop `docType` ('devis'|'facture'|'contrat').
 * Gère :
 *   - Liste filtrable par statut
 *   - Création (formulaire multi-lignes avec calcul des totaux en live)
 *   - Aperçu / téléchargement PDF
 *   - Envoi par email (avec PDF en pièce jointe)
 *   - Changement de statut
 *   - Conversion Devis accepté → Facture (uniquement docType=devis)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Plus, Download, Send, Trash2, FileText, Receipt, FileSignature, ArrowRight, CheckCircle2, X } from 'lucide-react';
import api from '../../../api';
import { toast } from 'sonner';

const fmtEur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

const TYPE_META = {
  devis: {
    label: 'Devis', icon: FileText,
    statuses: [
      { value: 'draft', label: 'Brouillon', color: 'bg-muted text-muted-foreground' },
      { value: 'sent', label: 'Envoyé', color: 'bg-cyan-500/10 text-cyan-600' },
      { value: 'accepted', label: 'Accepté', color: 'bg-emerald-500/10 text-emerald-600' },
      { value: 'refused', label: 'Refusé', color: 'bg-rose-500/10 text-rose-600' },
      { value: 'expired', label: 'Expiré', color: 'bg-amber-500/10 text-amber-600' },
      { value: 'converted', label: 'Converti en facture', color: 'bg-blue-500/10 text-blue-600' },
    ],
    defaultPayment: 'Acompte de 30% à la signature, solde à la fin de la prestation. Règlement par virement bancaire ou chèque à l\'ordre de Bruno Llorens.',
  },
  facture: {
    label: 'Facture', icon: Receipt,
    statuses: [
      { value: 'draft', label: 'Brouillon', color: 'bg-muted text-muted-foreground' },
      { value: 'sent', label: 'Envoyée', color: 'bg-cyan-500/10 text-cyan-600' },
      { value: 'paid', label: 'Payée', color: 'bg-emerald-500/10 text-emerald-600' },
      { value: 'overdue', label: 'En retard', color: 'bg-rose-500/10 text-rose-600' },
      { value: 'cancelled', label: 'Annulée', color: 'bg-muted text-muted-foreground' },
    ],
    defaultPayment: 'Virement bancaire',
  },
  contrat: {
    label: 'Contrat', icon: FileSignature,
    statuses: [
      { value: 'draft', label: 'Brouillon', color: 'bg-muted text-muted-foreground' },
      { value: 'sent', label: 'Envoyé', color: 'bg-cyan-500/10 text-cyan-600' },
      { value: 'signed', label: 'Signé', color: 'bg-emerald-500/10 text-emerald-600' },
      { value: 'cancelled', label: 'Annulé', color: 'bg-muted text-muted-foreground' },
    ],
    defaultPayment: '',
  },
};

const StatusBadge = ({ docType, status }) => {
  const meta = TYPE_META[docType]?.statuses.find(s => s.value === status);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta?.color || 'bg-muted'}`}>
      {meta?.label || status}
    </span>
  );
};

const EMPTY_LINE = { label: '', description: '', qty: 1, unit: 'u', unit_price: 0 };

export const B2BDocumentsTab = ({ docType, onChanged }) => {
  const meta = TYPE_META[docType];
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [prestationTypes, setPrestationTypes] = useState([]);

  // Création
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    items: [{ ...EMPTY_LINE }],
    notes: '',
    payment_terms: meta.defaultPayment,
    payment_method: docType === 'facture' ? 'Virement bancaire' : '',
    iban: '',
    validity_days: 30,
    due_days: 30,
    intervention_start: '',
    intervention_end: '',
    prestation_type: '',
  });
  const [saving, setSaving] = useState(false);

  // Envoi
  const [sendOpen, setSendOpen] = useState(false);
  const [sendDoc, setSendDoc] = useState(null);
  const [sendForm, setSendForm] = useState({ to_email: '', subject: '', body: '', cc: '' });
  const [sending, setSending] = useState(false);

  const totals = useMemo(() => {
    let ht = 0;
    for (const it of form.items) {
      ht += (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
    }
    return { ht, ttc: ht };
  }, [form.items]);

  const load = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const [d, c, m] = await Promise.all([
        api.b2bListDocuments({ type: docType, status }),
        api.b2bListClients(),
        api.b2bAgendaMeta().catch(() => ({ prestation_types: [] })),
      ]);
      setDocs(d.items || []);
      setClients(c.items || []);
      setPrestationTypes(m.prestation_types || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [docType, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => setForm({
    client_id: '',
    items: [{ ...EMPTY_LINE }],
    notes: '',
    payment_terms: meta.defaultPayment,
    payment_method: docType === 'facture' ? 'Virement bancaire' : '',
    iban: '',
    validity_days: 30,
    due_days: 30,
    intervention_start: '',
    intervention_end: '',
    prestation_type: '',
  });

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const updateLine = (idx, field, value) => {
    const lines = [...form.items];
    lines[idx] = { ...lines[idx], [field]: value };
    setForm({ ...form, items: lines });
  };

  const addLine = () => setForm({ ...form, items: [...form.items, { ...EMPTY_LINE }] });
  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const handleCreate = async () => {
    if (!form.client_id) {
      toast.error('Sélectionnez un client');
      return;
    }
    if (form.items.length === 0 || !form.items.some(it => it.label && it.label.trim())) {
      toast.error('Ajoutez au moins une ligne avec un libellé');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: docType,
        client_id: form.client_id,
        items: form.items
          .filter(it => it.label && it.label.trim())
          .map(it => ({
            label: it.label.trim(),
            description: it.description || null,
            qty: parseFloat(it.qty) || 0,
            unit: it.unit || 'u',
            unit_price: parseFloat(it.unit_price) || 0,
          })),
        notes: form.notes || null,
        payment_terms: form.payment_terms || null,
        payment_method: form.payment_method || null,
        iban: form.iban || null,
        validity_days: parseInt(form.validity_days) || 30,
        due_days: parseInt(form.due_days) || 30,
      };
      const created = await api.b2bCreateDocument(payload);
      // Si dates intervention ou prestation_type renseignés → patch schedule
      if (form.intervention_start || form.intervention_end || form.prestation_type) {
        try {
          await api.b2bScheduleDocument(created.id, {
            intervention_start: form.intervention_start ? new Date(form.intervention_start).toISOString() : '',
            intervention_end: form.intervention_end ? new Date(form.intervention_end).toISOString() : '',
            prestation_type: form.prestation_type || null,
          });
        } catch (err) {
          toast.error('Document créé mais erreur planification : ' + err.message);
        }
      }
      toast.success(`${meta.label} ${created.number} créé`);
      setCreateOpen(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.message || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { blob, filename } = await api.b2bDownloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${doc.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Téléchargement lancé');
    } catch (e) {
      toast.error('Échec du téléchargement');
    }
  };

  const openSend = async (doc) => {
    setSendDoc(doc);
    let toEmail = '';
    try {
      if (doc.client_id) {
        const c = await api.b2bGetClient(doc.client_id);
        toEmail = c?.client?.email || '';
      }
    } catch (e) { /* silent */ }
    setSendForm({
      to_email: toEmail,
      subject: `${meta.label} ${doc.number} — ${doc.client_name || ''}`.trim(),
      body: '',
      cc: '',
    });
    setSendOpen(true);
  };

  const handleSend = async () => {
    if (!sendForm.to_email || !sendForm.to_email.includes('@')) {
      toast.error('Email destinataire invalide');
      return;
    }
    setSending(true);
    try {
      const payload = {
        to_email: sendForm.to_email,
        subject: sendForm.subject || undefined,
        body: sendForm.body || undefined,
        cc: sendForm.cc || undefined,
      };
      const r = await api.b2bSendDocument(sendDoc.id, payload);
      toast.success(`Envoyé à ${r.to}`);
      setSendOpen(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.message || 'Échec envoi');
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (doc, newStatus) => {
    try {
      await api.b2bUpdateDocumentStatus(doc.id, { status: newStatus });
      toast.success(`Statut → ${newStatus}`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.message || 'Erreur changement statut');
    }
  };

  const handleConvert = async (doc) => {
    if (!window.confirm(`Convertir le devis ${doc.number} en facture ?`)) return;
    try {
      const facture = await api.b2bConvertDevisToFacture(doc.id);
      toast.success(`Facture ${facture.number} créée à partir du devis`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.message || 'Erreur conversion');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer définitivement le brouillon ${doc.number} ?`)) return;
    try {
      await api.b2bDeleteDocument(doc.id);
      toast.success('Brouillon supprimé');
      await load();
    } catch (e) {
      toast.error(e.message || 'Suppression refusée');
    }
  };

  return (
    <div className="space-y-4" data-testid={`b2b-${docType}-tab`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid={`b2b-${docType}-status-filter`}>
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {meta.statuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">{docs.length} {meta.label.toLowerCase()}{docs.length > 1 ? 's' : ''}</div>
        </div>
        <Button onClick={openCreate} data-testid={`b2b-${docType}-create`} disabled={clients.length === 0}>
          <Plus size={16} className="mr-1.5" /> Nouveau {meta.label.toLowerCase()}
        </Button>
      </div>

      {clients.length === 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="p-4 text-xs text-amber-700 dark:text-amber-400">
            Vous n'avez pas encore de client. Ajoutez d'abord un client dans l'onglet <strong>Clients</strong> avant de créer un {meta.label.toLowerCase()}.
          </CardContent>
        </Card>
      )}

      {loading && docs.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>
      ) : docs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-muted-foreground">
            Aucun {meta.label.toLowerCase()} pour ce filtre.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => {
            const isDraft = doc.status === 'draft';
            const isDevis = docType === 'devis';
            const canConvert = isDevis && (doc.status === 'accepted' || doc.status === 'sent') && doc.status !== 'converted';
            return (
              <Card key={doc.id} data-testid={`b2b-doc-${doc.id}`}>
                <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center">
                    <meta.icon size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-base">{doc.number}</div>
                      <StatusBadge docType={docType} status={doc.status} />
                      {doc.sent_count > 0 && (
                        <span className="text-[10px] text-muted-foreground">Envoyé {doc.sent_count}×</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {doc.client_name || '—'} · {fmtEur(doc.totals?.total_ttc)}
                      {doc.created_at && <> · créé le {new Date(doc.created_at).toLocaleDateString('fr-FR')}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Select value={doc.status} onValueChange={(v) => handleStatus(doc, v)}>
                      <SelectTrigger className="h-8 text-xs w-[140px]" data-testid={`b2b-doc-status-${doc.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {meta.statuses.map(s => (
                          <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => handleDownload(doc)} data-testid={`b2b-doc-download-${doc.id}`} title="Télécharger PDF">
                      <Download size={14} />
                    </Button>
                    <Button size="sm" onClick={() => openSend(doc)} data-testid={`b2b-doc-send-${doc.id}`} title="Envoyer par email">
                      <Send size={14} className="mr-1" /> Envoyer
                    </Button>
                    {canConvert && (
                      <Button size="sm" variant="outline" onClick={() => handleConvert(doc)} data-testid={`b2b-doc-convert-${doc.id}`} title="Convertir en facture">
                        <ArrowRight size={14} className="mr-1" /> Facturer
                      </Button>
                    )}
                    {isDraft && (
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(doc)} className="h-8 w-8 text-rose-600" data-testid={`b2b-doc-delete-${doc.id}`} title="Supprimer brouillon">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* DIALOG CRÉATION */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`b2b-${docType}-create-dialog`}>
          <DialogHeader>
            <DialogTitle>Nouveau {meta.label.toLowerCase()}</DialogTitle>
            <DialogDescription>Le numéro sera attribué automatiquement à la création.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid={`b2b-${docType}-form-client`}>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Lignes de prestation *</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid={`b2b-${docType}-form-add-line`}>
                  <Plus size={14} className="mr-1" /> Ajouter une ligne
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="p-3 border border-border rounded-lg space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-12 md:col-span-6">
                        <Input
                          placeholder="Libellé (ex: Cours plateau A2 — journée)"
                          value={it.label}
                          onChange={(e) => updateLine(idx, 'label', e.target.value)}
                          data-testid={`b2b-${docType}-line-${idx}-label`}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-2">
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="Qté"
                          value={it.qty}
                          onChange={(e) => updateLine(idx, 'qty', e.target.value)}
                          data-testid={`b2b-${docType}-line-${idx}-qty`}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-1">
                        <Input
                          placeholder="u"
                          value={it.unit}
                          onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="P.U. HT"
                          value={it.unit_price}
                          onChange={(e) => updateLine(idx, 'unit_price', e.target.value)}
                          data-testid={`b2b-${docType}-line-${idx}-price`}
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        {form.items.length > 1 && (
                          <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} className="h-9 w-9 text-rose-600">
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <Input
                      placeholder="Description (optionnel — apparaît sous le libellé)"
                      value={it.description}
                      onChange={(e) => updateLine(idx, 'description', e.target.value)}
                      className="text-xs"
                    />
                    <div className="text-right text-xs text-muted-foreground">
                      Total ligne : <strong>{fmtEur((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0))}</strong>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20 text-right">
                <div className="text-xs text-muted-foreground">Total HT (TVA non applicable, art. 293 B du CGI)</div>
                <div className="text-2xl font-black text-[hsl(var(--primary))]" data-testid={`b2b-${docType}-form-total`}>
                  {fmtEur(totals.ht)}
                </div>
              </div>
            </div>

            {docType === 'devis' && (
              <div>
                <Label>Validité (jours)</Label>
                <Input type="number" min="1" max="365" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
              </div>
            )}
            {docType === 'facture' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Échéance (jours)</Label>
                  <Input type="number" min="0" max="365" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} />
                </div>
                <div>
                  <Label>Mode de paiement</Label>
                  <Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="Virement bancaire" />
                </div>
                <div className="col-span-2">
                  <Label>IBAN (optionnel — sera affiché sur la facture)</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="FR76 ..." />
                </div>
              </div>
            )}

            {(docType === 'devis' || docType === 'contrat') && (
              <div>
                <Label>Conditions de règlement</Label>
                <Textarea rows={2} value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
              </div>
            )}

            {/* Planification agenda — dates d'intervention + type de prestation */}
            <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                📅 Planification dans l'agenda (optionnel)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Début intervention</Label>
                  <Input
                    type="datetime-local"
                    value={form.intervention_start}
                    onChange={(e) => setForm({ ...form, intervention_start: e.target.value })}
                    data-testid={`b2b-${docType}-form-intervention-start`}
                  />
                </div>
                <div>
                  <Label>Fin intervention</Label>
                  <Input
                    type="datetime-local"
                    value={form.intervention_end}
                    onChange={(e) => setForm({ ...form, intervention_end: e.target.value })}
                    data-testid={`b2b-${docType}-form-intervention-end`}
                  />
                </div>
              </div>
              <div>
                <Label>Type de prestation (colore l'événement dans l'agenda)</Label>
                <Select value={form.prestation_type || 'none'} onValueChange={(v) => setForm({ ...form, prestation_type: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid={`b2b-${docType}-form-prestation-type`}>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Non précisé —</SelectItem>
                    {prestationTypes.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value}>
                        <span className="inline-flex items-center gap-2">
                          <span style={{ backgroundColor: pt.color }} className="w-2 h-2 rounded-full inline-block" />
                          {pt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes complémentaires (optionnel)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Précisions sur la prestation, dates, lieu…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid={`b2b-${docType}-form-save`}>
              {saving ? 'Création…' : `Créer le ${meta.label.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG ENVOI EMAIL */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-2xl" data-testid="b2b-send-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer {sendDoc?.number} par email</DialogTitle>
            <DialogDescription>Le PDF sera attaché automatiquement à l'email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Destinataire *</Label>
              <Input
                type="email"
                value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                placeholder="contact@auto-ecole.fr"
                data-testid="b2b-send-to"
              />
            </div>
            <div>
              <Label>Copie carbone (CC, optionnel)</Label>
              <Input
                type="email"
                value={sendForm.cc}
                onChange={(e) => setSendForm({ ...sendForm, cc: e.target.value })}
                placeholder="Pour vous mettre en copie de votre envoi"
              />
            </div>
            <div>
              <Label>Sujet</Label>
              <Input
                value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                data-testid="b2b-send-subject"
              />
            </div>
            <div>
              <Label>Message personnalisé (optionnel)</Label>
              <Textarea
                rows={5}
                value={sendForm.body}
                onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                placeholder="Laissez vide pour utiliser le message standard. Sinon, vous pouvez écrire en HTML (ex: <p>Bonjour…</p>)."
                data-testid="b2b-send-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>Annuler</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="b2b-send-confirm">
              <Send size={14} className="mr-1.5" />
              {sending ? 'Envoi…' : "Envoyer maintenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
